import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, Mail, MapPin, Clock, Send, Calendar,
  MessageCircle, ArrowRight, Loader2, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { BRAND } from '../data/mock';

const ContactPage = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    console.log('Contact form submitted:', formData);
    
    toast({
      title: "Message Sent!",
      description: "We'll get back to you within 24 hours."
    });
    
    setIsSubmitted(true);
    setIsSubmitting(false);
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#2F5233] to-[#1e3621] overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-72 h-72 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Get In Touch
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Have questions about our services or need to schedule an appointment? 
            We're here to help.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-[#2F5233]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#2F5233]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                      <a href="tel:+917353196539" className="text-[#2F5233] hover:underline">
                        +91 73531 96539
                      </a>
                      <p className="text-sm text-gray-500 mt-1">Mon-Sun (except Tue)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-[#2F5233]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#2F5233]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                      <a href="mailto:drjenicebhatia@gmail.com" className="text-[#2F5233] hover:underline">
                        drjenicebhatia@gmail.com
                      </a>
                      <p className="text-sm text-gray-500 mt-1">We reply within 24 hours</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-[#2F5233]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#2F5233]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Clinic Addresses</h3>
                      <div className="space-y-3 text-gray-600">
                        {BRAND.clinics.map((clinic) => (
                          <a key={clinic.name} href={clinic.mapUrl} target="_blank" rel="noopener noreferrer" className="block hover:text-[#2F5233] hover:underline">
                            <span className="font-medium">{clinic.name}</span><br />
                            {clinic.full}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-[#2F5233]/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#2F5233]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Clinic Hours</h3>
                      <p className="text-gray-600">
                        Mon, Wed - Fri: 11 AM - 2 PM & 4 PM - 7 PM
                        <br />
                        Sat & Sun: 11 AM - 3 PM
                      </p>
                      <p className="text-red-500 text-sm mt-1">Closed on Tuesdays</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* WhatsApp Button */}
              <a
                href="https://wa.me/917353196539?text=Hi%2C%20I%20would%20like%20to%20inquire%20about%20consultation"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Chat on WhatsApp
                </Button>
              </a>
            </div>
            
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  {isSubmitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                      <p className="text-gray-600 mb-8">
                        Thank you for reaching out. We'll get back to you within 24 hours.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setIsSubmitted(false);
                            setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
                          }}
                        >
                          Send Another Message
                        </Button>
                        <Link to="/book">
                          <Button className="bg-[#2F5233] hover:bg-[#2F5233]/90">
                            <Calendar className="w-4 h-4 mr-2" />
                            Book Appointment
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">Send us a Message</h2>
                      <p className="text-gray-600 mb-8">
                        Fill out the form below and we'll get back to you as soon as possible.
                      </p>
                      
                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="name">Full Name *</Label>
                            <Input
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              placeholder="Your name"
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email">Email Address *</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="your@email.com"
                              required
                            />
                          </div>
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone Number</Label>
                            <Input
                              id="phone"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="+91 98765 43210"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="subject">Subject *</Label>
                            <Input
                              id="subject"
                              name="subject"
                              value={formData.subject}
                              onChange={handleChange}
                              placeholder="How can we help?"
                              required
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="message">Message *</Label>
                          <Textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Tell us more about your inquiry..."
                            rows={5}
                            required
                          />
                        </div>
                        
                        <Button
                          type="submit"
                          className="w-full bg-[#2F5233] hover:bg-[#2F5233]/90 text-white"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Message
                            </>
                          )}
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Find Us</h2>
            <p className="text-gray-600 mt-2">Visit our clinic for in-person consultations</p>
          </div>
          
          <div className="rounded-2xl overflow-hidden shadow-xl h-[400px] bg-gray-200">
            {/* Google Maps Embed */}
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3503.7847076768373!2d77.33615757549373!3d28.568629075692714!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce5b6632b240d%3A0x4c0eb8fee56fca8a!2sDr.%20Jenice%20Bhatia%20Clinic!5e0!3m2!1sen!2sin!4v1705331000000!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Dr. Jenice Bhatia Clinic Location"
            />
          </div>
          <div className="text-center mt-6">
            <p className="text-gray-600 font-medium">525, Mall Road, Near Sector 37 Water Tank</p>
            <p className="text-gray-500">Sector 29, Noida</p>
            <a
              href={BRAND.clinics[0].mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center mt-4 text-[#2F5233] hover:underline"
            >
              Open in Google Maps
              <ArrowRight className="w-4 h-4 ml-1" />
            </a>
            <a
              href={BRAND.clinics[1].mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="block mt-3 text-[#2F5233] hover:underline"
            >
              {BRAND.clinics[1].name}: {BRAND.clinics[1].full}
              <ArrowRight className="inline-block w-4 h-4 ml-1" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#2F5233] to-[#1e3621]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Book Your Consultation?
          </h2>
          <p className="text-white/80 mb-8">
            Skip the wait - book your appointment online in just a few clicks.
          </p>
          <Link to="/book">
            <Button size="lg" className="bg-white text-[#2F5233] hover:bg-white/90">
              <Calendar className="w-5 h-5 mr-2" />
              Book Appointment
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
